import { AssessmentStatusDTO } from '@ketch-com/figurehead'
import Box from '@mui/material/Box'
import { Banner, Chip, Icon, LinearProgress, theme } from '@ketch-com/deck'
import Typography from '@mui/material/Typography'
import { ProcessingActivityContext } from '../context/ProcessingActivityContext'
import React, { useContext } from 'react'
import { RoutesManager } from 'utils/routing/routesManager'
import { useUser } from 'api/users/queries/useUser'
import { Assessment } from '@ketch-com/windlass/dist/assessments/assessments'
import { AssessmentWidgetDetailsActionSheet } from './widgets/AssessmentWidgetDetailsActionSheet'

type Props = {
  assessment: Assessment
  borderBottom?: boolean
}

export const AssessmentDetails: React.FC<Props> = ({ assessment, borderBottom }) => {
  const { navigate } = useContext(ProcessingActivityContext)
  const { data: approver } = useUser({ params: { userId: assessment.attributes?.approvedBy || '' } })

  const approvedBy = `${approver?.firstName.charAt(0).toUpperCase()}${approver?.firstName.slice(1)} ${approver?.lastName
    .charAt(0)
    .toUpperCase()}${approver?.lastName.slice(1)}`

  const { completedQuestions, totalQuestions, status } = assessment?.attributes || {}

  const percentComplete = Math.round(((completedQuestions || 0) / (totalQuestions || 1)) * 100)

  const finishedInProgress =
    assessment?.attributes?.status === AssessmentStatusDTO.InProgressAssessmentStatus &&
    assessment?.attributes?.completedQuestions === assessment?.attributes?.totalQuestions

  const handleTemplateClick = () => {
    navigate(RoutesManager.policyCenter.assessments.edit.getURL({ code: assessment.id || '' }))
  }

  return (
    <Box
      display="flex"
      flexDirection="column"
      gap={3}
      width="100%"
      sx={{ borderBottom: borderBottom ? `1px solid ${theme.palette.Common.Divider}` : 'none', paddingY: 1.5 }}
    >
      {/* Notice for an assessment which has expired */}
      {status === AssessmentStatusDTO.ExpiredAssessmentStatus && (
        <Banner variant="standard" severity="info" isInfoBox>
          The assessment was conducted some time ago and might now need to be revised to ensure it's up to date.
        </Banner>
      )}

      <Box display="flex" gap={1.5} mt={1.5} justifyContent="space-between" width="100%">
        <Box display="flex" flexDirection="column" gap={1.5} alignItems="center">
          <Chip
            label={
              <Box
                sx={{
                  whiteSpace: 'nowrap',
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  maxWidth: '230px',
                }}
              >
                {assessment.attributes?.name}
              </Box>
            }
            clickable
            deleteIcon={<Icon name="OArrowCRight" />}
            onDelete={handleTemplateClick}
            onClick={handleTemplateClick}
            sx={{ width: 'fit-content', maxWidth: '100%' }}
          />
          <Box display="flex" gap={0.5} alignItems="center" paddingX={1.5}>
            {status === AssessmentStatusDTO.InProgressAssessmentStatus ? (
              <>
                <LinearProgress
                  color="primary"
                  sx={{ width: 64, height: 8, borderRadius: 24, backgroundColor: theme.palette.Text.Disabled }}
                  variant="determinate"
                  value={percentComplete}
                />
                <Typography variant="label">{percentComplete}%</Typography>
                <Typography color={theme.palette.Text.Secondary}>
                  {completedQuestions}/{totalQuestions} Questions
                </Typography>
              </>
            ) : status === AssessmentStatusDTO.ApprovedAssessmentStatus ? (
              <>
                <Icon name="FCheckRound" width={24} height={24} />
                <Box display="flex" flexDirection="column">
                  <Typography variant="smallLabel">Approved</Typography>
                  <Typography variant="smallBody">by {approvedBy}</Typography>
                </Box>
              </>
            ) : status === AssessmentStatusDTO.InReviewAssessmentStatus ? (
              <>
                <Icon name="FEyeShow" iconColor={theme.palette.Action.PrimaryDark} width={24} height={24} />
                <Typography variant="smallLabel" color={theme.palette.Action.PrimaryDark}>
                  In Review
                </Typography>
              </>
            ) : null}
          </Box>
          {finishedInProgress && status === AssessmentStatusDTO.InProgressAssessmentStatus && (
            <Banner variant="standard" severity="info" isInfoBox sx={{ mt: 1 }}>
              The assessment is complete but needs review before it can be approved.
            </Banner>
          )}
        </Box>
        <AssessmentWidgetDetailsActionSheet assessmentId={assessment.id || ''} pending={false} />
      </Box>
    </Box>
  )
}
